import React, { useEffect, useState } from "react";
import { PageLoader } from "../../components/shared";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { toast } from "sonner";
import api, { apiErr } from "../../lib/api";

function NumField({ label, value, onChange, suffix, testid }) {
  return (
    <div>
      <Label className="text-sm">{label}</Label>
      <div className="relative mt-1.5">
        <Input type="number" value={value ?? 0} onChange={(e) => onChange(+e.target.value)} data-testid={testid} className="pr-10" />
        {suffix && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">{suffix}</span>}
      </div>
    </div>
  );
}

export default function AdminConfig() {
  const [c, setC] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/admin/config").then((r) => setC(r.data)); }, []);
  const set = (k, v) => setC({ ...c, [k]: v });
  const setR = (k, v) => setC({ ...c, rewards: { ...c.rewards, [k]: v } });

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/config", {
        gst_percent: c.gst_percent, platform_commission_percent: c.platform_commission_percent,
        convenience_fee_percent: c.convenience_fee_percent, convenience_fee_flat: c.convenience_fee_flat,
        gateway_charge_percent: c.gateway_charge_percent, rewards: c.rewards,
      });
      toast.success("Business rules updated. Applied instantly across the platform.");
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  if (!c) return <PageLoader />;

  return (
    <div className="max-w-3xl">
      <div className="overline mb-1">No-code control</div>
      <h1 className="font-heading text-3xl md:text-4xl font-semibold tracking-tight mb-6">Business Rules</h1>

      <div className="rounded-xl border border-border bg-card p-6 mb-5">
        <h2 className="font-heading text-lg font-semibold mb-4">Fees & Taxes</h2>
        <div className="grid sm:grid-cols-2 gap-4">
          <NumField label="GST" value={c.gst_percent} onChange={(v) => set("gst_percent", v)} suffix="%" testid="cfg-gst" />
          <NumField label="Platform commission" value={c.platform_commission_percent} onChange={(v) => set("platform_commission_percent", v)} suffix="%" testid="cfg-commission" />
          <NumField label="Convenience fee" value={c.convenience_fee_percent} onChange={(v) => set("convenience_fee_percent", v)} suffix="%" testid="cfg-conv-pct" />
          <NumField label="Convenience fee (flat)" value={c.convenience_fee_flat} onChange={(v) => set("convenience_fee_flat", v)} suffix="₹" testid="cfg-conv-flat" />
          <NumField label="Gateway charge" value={c.gateway_charge_percent} onChange={(v) => set("gateway_charge_percent", v)} suffix="%" testid="cfg-gateway" />
        </div>
      </div>

      <div className="rounded-xl border border-border bg-card p-6 mb-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-lg font-semibold">Event Credits / Rewards</h2>
          <label className="flex items-center gap-2 text-sm"><Switch checked={c.rewards.enabled} onCheckedChange={(v) => setR("enabled", v)} /> Enabled</label>
        </div>
        <div className="grid sm:grid-cols-3 gap-4">
          <NumField label="Points per ₹100" value={c.rewards.points_per_100} onChange={(v) => setR("points_per_100", v)} testid="cfg-ppr" />
          <NumField label="Point value" value={c.rewards.point_value} onChange={(v) => setR("point_value", v)} suffix="₹" />
          <NumField label="Min redeem points" value={c.rewards.min_redeem_points} onChange={(v) => setR("min_redeem_points", v)} />
          <NumField label="Max redeem" value={c.rewards.max_redeem_percent} onChange={(v) => setR("max_redeem_percent", v)} suffix="%" />
          <NumField label="Expiry" value={c.rewards.expiry_days} onChange={(v) => setR("expiry_days", v)} suffix="days" />
          <NumField label="New-user bonus" value={c.rewards.new_user_bonus} onChange={(v) => setR("new_user_bonus", v)} suffix="pts" />
        </div>
      </div>

      <Button onClick={save} disabled={saving} className="rounded-full" data-testid="save-config">{saving ? "Saving…" : "Save business rules"}</Button>
    </div>
  );
}
